/**
 * Pacing policies over `env.pump`. Each answers the same question — when the page's own loop is
 * starved, how much game time to inject, and where — and each is a `PumpPolicy`: call `onWake()`
 * once per poll wake (an `observe` `onWake`), `stop()` when the window closes.
 */
import type { EnvironmentQueries, ProcessResolver, ProcessTarget, PumpableProcess } from './environment-lookup.js';
import { subscribeProcessTick } from './environment-ticks.js';

/** One pacing policy: `onWake()` once per poll wake (an `observe` `onWake`); `stop()` when the window closes — it injects nothing after. */
export interface PumpPolicy {
  onWake(): void;
  stop(): void;
}

/** The pacing policies every `MockEnvironment` offers as `env.pumps`, mixed into it by `initializer`. */
export interface PacingQueries {
  pumps: {
    /**
     * Inject the WALL time that elapsed since the last wake (capped at `maxGapS`, default 1) in `sliceS`
     * slices (default 0.05), so game time tracks wall time however rarely the page wakes. `now` is an
     * injectable wall clock (default `Date.now`); it exists so a test can control wall time. Both sizes must
     * be finite and > 0.
     */
    gap(target?: ProcessTarget, opts?: { maxGapS?: number; sliceS?: number; now?: () => number }): PumpPolicy;
    /**
     * Inject only when `gameTimeMs(target)` moved less than `starvedBelowMs` (default 5) since the last wake:
     * one fine slice (default 1/60) while `busy()`, else a coarse one (default 0.25). Nothing at all while the
     * page's own loop is moving the game. All three must be finite and > 0 (a threshold of 0 would never see a
     * starved clock: the policy would be off).
     */
    whenStarved(target?: ProcessTarget, opts?: { busy?: () => boolean; fineSliceS?: number; coarseSliceS?: number; starvedBelowMs?: number }): PumpPolicy;
    /**
     * Keep `authority`'s game clock level with `follower`'s: on every follower tick inject the shortfall in
     * `frameMs` frames (default 1000/60, finite > 0), at most `maxTopupFrames` (default 15, a whole number ≥ 1)
     * per tick. The shortfall counts what the authority already HAS — its game time, plus pumped time a
     * fixed-step authority holds in its step carry — so `pace` never injects past the follower: while the
     * authority's own loop is starved it is never ahead (a fixed-step one trails by less than one of its steps).
     * When both loops run, the authority leads only by what its own loop ran since the follower's last tick: at
     * most one of its ticks (variable-step) or steps (fixed-step) while it ticks no faster than the follower.
     * Each target must name ONE process, and not the same one (a process must never be updated from inside its
     * own tick).
     */
    pace(opts: { authority: ProcessTarget; follower: ProcessTarget; frameMs?: number; maxTopupFrames?: number }): PumpPolicy;
  };
}

/** Float slack on a frame: a shortfall a rounding error short of one frame still owes that frame. */
const FRAME_SLACK_MS = 1e-6;

/** Every size a policy takes is checked when the policy is made: a zero, negative or non-finite one would pump nothing, or pump forever. */
const requirePositive = (policy: string, knob: string, unit: string, value: number) => {
  if (!(Number.isFinite(value) && value > 0)) throw new Error(`[test-utils] pumps.${policy}: ${knob} must be a finite number of ${unit} > 0 (got ${value})`);
};

/**
 * Builds `env.pumps` for one environment over its resolver, its queries (`pump`, `gameTimeMs`) and its pump's
 * carry (`createEnvironmentQueries`' `pumpCarrySec`, which `initializer` hands here).
 */
export const createPacingQueries = (
  resolver: ProcessResolver,
  queries: EnvironmentQueries,
  pumpCarrySec: (process: PumpableProcess) => number,
): PacingQueries => {
  const oneProcess = (role: 'authority' | 'follower', target: ProcessTarget): PumpableProcess => {
    const processes = [...new Set(resolver.resolveTargets(target))];
    if (processes.length !== 1) {
      throw new Error(`[test-utils] pumps.pace: the ${role} must name ONE process; ${resolver.describeTarget(target)} names ${processes.length}.`);
    }
    return processes[0];
  };

  return {
    pumps: {
      gap(target, { maxGapS = 1, sliceS = 0.05, now = Date.now } = {}) {
        requirePositive('gap', 'maxGapS', 'seconds', maxGapS);
        requirePositive('gap', 'sliceS', 'seconds', sliceS);
        resolver.resolveTargets(target); // fail loud now, not on the first wake
        let live = true;
        let last = now();
        return {
          onWake() {
            if (!live) return;
            const t = now();
            let gap = Math.min((t - last) / 1000, maxGapS);
            last = t;
            while (gap > 1e-9) {
              const slice = Math.min(sliceS, gap);
              queries.pump(slice, target);
              gap -= slice;
            }
          },
          stop() { live = false; },
        };
      },
      whenStarved(target, { busy, fineSliceS = 1 / 60, coarseSliceS = 0.25, starvedBelowMs = 5 } = {}) {
        requirePositive('whenStarved', 'fineSliceS', 'seconds', fineSliceS);
        requirePositive('whenStarved', 'coarseSliceS', 'seconds', coarseSliceS);
        // 0 is not "never starved", it is OFF: a clock that moved less than 0 ms does not exist.
        requirePositive('whenStarved', 'starvedBelowMs', 'milliseconds', starvedBelowMs);
        // Starvation is read off ONE clock: a target that names nothing, or several processes, fails here, now.
        resolver.oneProcess(target, 'pumps.whenStarved');
        let live = true;
        let last = queries.gameTimeMs(target);
        return {
          onWake() {
            if (!live) return;
            if (queries.gameTimeMs(target) - last < starvedBelowMs) queries.pump(busy?.() ? fineSliceS : coarseSliceS, target);
            last = queries.gameTimeMs(target);
          },
          stop() { live = false; },
        };
      },
      pace({ authority, follower, frameMs = 1000 / 60, maxTopupFrames = 15 }) {
        requirePositive('pace', 'frameMs', 'milliseconds', frameMs);
        if (!(Number.isInteger(maxTopupFrames) && maxTopupFrames >= 1)) {
          throw new Error(`[test-utils] pumps.pace: maxTopupFrames must be a whole number ≥ 1 (got ${maxTopupFrames})`);
        }
        const authorityProcess = oneProcess('authority', authority);
        const followerProcess = oneProcess('follower', follower);
        if (authorityProcess === followerProcess) {
          throw new Error('[test-utils] pumps.pace: authority and follower are the same process — a process must never be updated from inside its own tick; use pumps.whenStarved for one process.');
        }
        // What the authority already HAS: the time it ran, plus pumped time a fixed-step authority holds in its
        // step carry — counted, so a shortfall pumped into the carry is never owed (and pumped) a second time.
        const hasMs = () => queries.gameTimeMs(authority) + pumpCarrySec(authorityProcess) * 1000;
        let followerMs = 0;
        const authorityStart = hasMs();
        const stop = subscribeProcessTick(followerProcess, (deltaMs, phase) => {
          if (phase !== 'before') return;
          followerMs += deltaMs;
          let owedMs = followerMs - (hasMs() - authorityStart);
          for (let frames = 0; owedMs >= frameMs - FRAME_SLACK_MS && frames < maxTopupFrames; frames += 1) {
            queries.pump(frameMs / 1000, authority);
            owedMs -= frameMs;
          }
        });
        return { onWake: () => undefined, stop };
      },
    },
  };
};
